import React, { useEffect, useMemo, useState } from 'react';
import queryString from 'query-string';
import { AppState } from 'stores/type';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { Splash } from 'views/components/molecules/Splash';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';

/**
 * ペアリング設定ページ
 *
 * ネイティブアプリの SFSafariViewController から開かれる。
 * localStorage に保存されている iphone_uid（webclip で Configuration Profile インストール済みの場合）を
 * Custom URL Scheme でネイティブアプリに返す中継ページ。
 *
 * クエリパラメータ:
 *   - callback_url: ペアリング完了後に戻る Custom URL Scheme（例: deploygate://paired）
 *                   セキュリティのため deploygate:// スキームのみ許可する
 *
 * 動作:
 *   - iphone_uid あり → callback_url に iphone_uid を付けてリダイレクト（native app 側で pair API を呼ぶ）
 *   - iphone_uid なし → callback_url に iphone_uid なしでリダイレクト（native app 側で Profile インストールに誘導）
 */

// オープンリダイレクト脆弱性を防ぐため、許可するスキームを限定する
const ALLOWED_CALLBACK_SCHEME = 'deploygate://';

export const PairingSetupPage: React.FC = () => {
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  // CheckSessionRouter で localStorage からの復元が完了したかを示すフラグ
  // 復元完了前にリダイレクトすると iphone_uid なしになってしまうので明示的に待つ
  const fetched = useSelector((state) => state.deviceState.fetched);
  const [redirected, setRedirected] = useState(false);

  // セキュリティのため callback_url を検証する
  // - 配列で渡された場合は最初の要素を使う
  // - deploygate:// 以外のスキームは拒否する（オープンリダイレクト対策）
  const callbackUrl = useMemo(() => {
    const parsed = queryString.parse(location.search)['callback_url'];
    const url = Array.isArray(parsed) ? parsed[0] : parsed;
    if (typeof url === 'string' && url.startsWith(ALLOWED_CALLBACK_SCHEME)) {
      return url;
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!fetched || redirected) return;
    if (!callbackUrl) return;

    setRedirected(true);

    if (device && device.uid) {
      // webclip で Configuration Profile インストール済み → iphone_uid を返す
      const separator = callbackUrl.includes('?') ? '&' : '?';
      location.replace(
        `${callbackUrl}${separator}iphone_uid=${encodeURIComponent(device.uid)}`
      );
    } else {
      // Configuration Profile 未インストール → iphone_uid なしで返す
      location.replace(callbackUrl);
    }
  }, [device, fetched, callbackUrl, redirected]);

  return (
    <DefaultTemplate headerText="">
      <Splash />
    </DefaultTemplate>
  );
};
